import { ROUTES } from '@app/constants'
import { GetCountryByIpDocument, UserRoles } from '@app/graphql'
import { newUserOnboardingPath, onboardingRedirectUrl } from '@app/helpers/auth'
import { AppMenuState, CurrentCountryState, CurrentUserState, resetCurrentCountryState } from '@app/storage'
import { FC, lazy, Suspense, useEffect, useMemo } from 'react'
import { withSuspense } from '@app/components/withSuspense'
import ProfileSkeleton from '@app/screens/ProfileSkeleton'
import TipsterProfileSkeleton from '@app/ui/TipsterProfileSkeleton'
import TipsterSubscriptionsSkeleton from '@app/ui/TipsterSubscriptionsSkeleton'
import { TournamentsPageSkeleton } from '@app/screens/Tournament/Tournaments/components/Skeleton'
import CompetitionVotesSkeleton from '@app/screens/Tournament/CompetitionVotes/components/CompetitionVotesSkeleton'
import { BrowserRouter, MemoryRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { useSnapshot } from 'valtio'
import MetaPixel from '@app/components/MetaPixel'
import TwitterPixel from '@app/components/TwitterPixel'
import EmbedBridge from '@app/components/EmbedBridge'
import { SignupPromptModal } from '@app/ui'
import { EntryScreen } from '@app/screens'
import AppMenu from '@app/components/AppMenu/AppMenu'
import { SupportChatProvider } from '@app/components/SupportChat'
import SubscriptionsRoute from '@app/components/SubscriptionsRoute'

// Components lazy loading
import PublicChannelScreen from '@app/screens/Channels/PublicChannel'
import PrivateChannelScreen from '@app/screens/Channels/PrivateChannel'
import { useLazyQuery } from '@apollo/client'
import { useUpdateLangByCountry } from '@app/hooks/useUpdateLangByCountry'
import { Dashboard } from '@app/screens/Dashboard'

const CommentsScreen = lazy(() => import('@app/screens/Comments/Comments'))
const AppSettingsScreen = lazy(() => import('@app/screens/AppSettings'))
const ContactFormScreen = lazy(() => import('@app/screens/ContactForm'))
const MatchesListScreen = lazy(() => import('@app/screens/Matches/MatchesList'))
const SavedTipsScreen = lazy(() => import('@app/screens/SavedTips'))
const NotificationsSettingsScreen = lazy(() => import('@app/screens/NotificationsSettings'))
const InboxScreen = lazy(() => import('@app/screens/Inbox/Inbox'))
const ProfileBase = lazy(() => import('@app/screens/Profile'))
const Profile = withSuspense(ProfileBase, <ProfileSkeleton />)
const ChannelProfileScreen = lazy(() => import('@app/screens/ChannelProfile/ChannelProfile'))
const MatchesVotedScreen = lazy(() => import('@app/screens/MatchesVoted/MatchesVoted'))
const BannedScreen = lazy(() => import('@app/screens/Banned'))
const PaymentScreen = lazy(() => import('@app/screens/Payment/Payment'))
const PaymentResultScreen = lazy(() => import('@app/screens/Payment/PaymentResult'))
const LegalScreen = lazy(() => import('@app/screens/Legal/Legal'))
const ProtectedRoute = lazy(() => import('@app/components/ProtectedRoute'))

const AdvertisementsScreen = lazy(() => import('@app/screens/Advertisements/Advertisements'))
const BonusesScreen = lazy(() => import('@app/screens/Bonuses/Bonuses'))
const BonusCreateScreen = lazy(() => import('@app/screens/Bonuses/BonusCreate'))
const BonusEditScreen = lazy(() => import('@app/screens/Bonuses/BonusEdit'))
const AdvertisementDetailScreen = lazy(() => import('@app/screens/Advertisements/AdvertisementDetail'))
const BookiesScreen = lazy(() => import('@app/screens/Bookies/Bookies'))
const RgBannersScreen = lazy(() => import('@app/screens/RgBanners/RgBanners'))
const BookieFormScreen = lazy(() => import('@app/screens/Bookies/BookieForm'))
const UserSubscriptions = lazy(() => import('@app/screens/UserSubscriptions'))
const TipsterSubscriptionsBase = lazy(() => import('@app/screens/TipsterSubscriptions'))
const TipsterSubscriptions = withSuspense(TipsterSubscriptionsBase, <TipsterSubscriptionsSkeleton />)
const TipsterSubscribersList = lazy(() => import('@app/screens/TipsterSubscribersLIst/TipsterSubscribersList'))
const TipsterListScreen = lazy(() => import('@app/screens/TipsterList/TipsterList'))
const UserSubscribersList = lazy(() => import('@app/screens/UserSubscribersList/UserSubscribersList'))
const InstallScreen = lazy(() => import('@app/screens/InstallPage'))
const ChannelListScreen = lazy(() => import('@app/screens/channelList/ChannelList'))
const AuthCallbackScreen = lazy(() => import('@app/screens/AuthCallback'))
const LoginRoutes = lazy(() => import('@app/screens/Login/LoginRoutes'))
const TipsterOnboardingRoutes = lazy(() => import('@app/screens/Login/TipsterOnboarding'))
const Leaderboard = lazy(() => import('@app/screens/Leaderboard/Leaderboard'))
const CreateTournamentForm = lazy(() => import('@app/screens/Tournament/CreateTournament/CreateTournament'))
const TournamentsBase = lazy(() => import('@app/screens/Tournament/Tournaments/Tournaments'))
const Tournaments = withSuspense(TournamentsBase, <TournamentsPageSkeleton />)
const CompetitionsLeaderboard = lazy(() => import('@app/screens/Tournament/Leaderboard/Leaderboard'))
const TipsterCompetitionManagement = lazy(() => import('@app/screens/Tournament/Leaderboard/TipsterCompetitionManagement'))
const CompetitionVotesBase = lazy(() => import('@app/screens/Tournament/CompetitionVotes/CompetitionVotes'))
const CompetitionVotes = withSuspense(CompetitionVotesBase, <CompetitionVotesSkeleton />)
const NotificationsSettingsDetailsScreen = lazy(() => import('@app/screens/NotificationsSettingsDetails'))
const MagicLinkScreen = lazy(() => import('@app/screens/MagicLink'))
const MagicLinkSentScreen = lazy(() => import('@app/screens/MagicLinkSent'))
const MatchTrackingScreen = lazy(() => import('@app/screens/MatchTracking/MatchTracking'))
const TelegramAutoShareScreen = lazy(() => import('@app/screens/TelegramAutoShare'))
const VipInviteRedeemScreen = lazy(() => import('@app/screens/VipInviteRedeem/VipInviteRedeem'))
const StreamListScreen = lazy(() => import('@app/screens/StreamList'))

// A brand-new user (valid auth session, no account yet) can land on any route
// after the hard reload that follows SSO/Cognito login — not just /login or the
// root entry screen. This catches that case regardless of where they land and
// sends them into onboarding via react-router navigation (not window.location,
// so it stays safe inside the casino MemoryRouter iframe case too).
const OnboardingRedirectGuard: FC = () => {
  const { initiateOnboarding, id, userType, onboardingStage, userDataLoading } = useSnapshot(CurrentUserState)
  const navigate = useNavigate()

  useEffect(() => {
    if (!initiateOnboarding) return
    const target = onboardingRedirectUrl(id, userType, onboardingStage, userDataLoading)
    if (target && target !== window.location.pathname) {
      navigate(target, { replace: true })
    }
  }, [ initiateOnboarding, id, userType, onboardingStage, userDataLoading, navigate ])

  return null
}

interface AppRoutesProps {}

const AppRoutes: FC<AppRoutesProps> = () => {
  const userState = useSnapshot(CurrentUserState)
  const { opened: appMenuOpened } = useSnapshot(AppMenuState)
  const {
    hasOnboarded,
    id,
    userType,
    banned,
    initiateOnboarding,
    onboardingStage,
    isAdmin,
    isTipster,
    isTipsterAdmin,
    userDataLoading,
    isGuestTipster,
  } = userState
  if([ `${window.location.pathname}/`,`${window.location.pathname}` ].includes(ROUTES.tipsterOnboarding.short)) {
    localStorage.setItem('onboardingFlow', UserRoles.Tipster)
  }
  const isAllowed = useMemo(
    () => !!id || (sessionStorage.getItem('incognitoMode') === 'true') || initiateOnboarding,
    [ id, initiateOnboarding ]
  )

  const { id: currentCountryId } = useSnapshot(CurrentCountryState)
  const [ getCountryByIp ] = useLazyQuery(GetCountryByIpDocument)
  const { UpdateLangByCountry } = useUpdateLangByCountry()

  useEffect(() => {
    getCountryByIp().then(({ data }) => {
      resetCurrentCountryState(data?.countryByIp ?? {})
    }).catch((e) => {
      console.error(e)
      CurrentCountryState.loaded = true
    })
  }, [ getCountryByIp ])

  useEffect(() => {
    if (currentCountryId) UpdateLangByCountry()
  }, [ currentCountryId, UpdateLangByCountry ])


  const entryScreenRedirect = useMemo(() => {
    if (initiateOnboarding && !id) {
      return newUserOnboardingPath()
    }
    if(initiateOnboarding || (isAllowed && !hasOnboarded)) {
      return onboardingRedirectUrl(id, userType, onboardingStage, userDataLoading) || ROUTES.feed.short
    }
    return ROUTES.feed.short
  }, [ id, initiateOnboarding, userType, onboardingStage, isAllowed, hasOnboarded, userDataLoading ])

  const SpecialRoutes = useMemo(() => {
    if(banned) return <Route path="*" element={<BannedScreen/>} />

    return (
      <>
        <Route
          index
          element={
            <ProtectedRoute redirectPath={entryScreenRedirect} isAllowed={!isAllowed}>
              <EntryScreen/>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.authCallback.short}
          element={
            // No ProtectedRoute here: any visitor (including guest-mode users)
            // must reach AuthCallbackScreen so the magic link sign-in can complete.
            // AuthCallbackScreen itself navigates to /feed once auth succeeds.
            <AuthCallbackScreen/>
          }
        />
        <Route element={<ProtectedRoute redirectPath={ROUTES.root} isAllowed={isAllowed && hasOnboarded} />}>
          <Route
            path={ROUTES.feed.short}
            element={<PublicChannelScreen/>}
          />
          <Route
            path={ROUTES.channel.short}
            element={<PrivateChannelScreen/>}
          />
        </Route>
        <Route element={<ProtectedRoute redirectPath={ROUTES.root} isAllowed={isAllowed && hasOnboarded} />}>
          <Route
            path={ROUTES.appSettings.short}
            element={<AppSettingsScreen/>}
          />
          <Route
            path={ROUTES.StreamList.short}
            element={<StreamListScreen/>}
          />
          <Route
            path={ROUTES.TelegramAutoShare.short}
            element={<TelegramAutoShareScreen/>}
          />
          <Route
            path={ROUTES.contactForm.short}
            element={<ContactFormScreen/>}
          />
          <Route
            path={ROUTES.matchesList.short}
            element={<MatchesListScreen/>}
          />
          <Route
            path={ROUTES.savedTips.short}
            element={<SavedTipsScreen/>}
          />
          <Route
            path={ROUTES.notifications.short}
            element={<NotificationsSettingsScreen/>}
          />

          <Route
            path={ROUTES.notificationsDetails.short}
            element={<NotificationsSettingsDetailsScreen/>}
          />
          <Route
            path={ROUTES.inbox.short}
            element={<InboxScreen/>}
          />
          <Route
            path={ROUTES.profile.full}
            element={<Profile/>}
          />
          <Route
            path={ROUTES.channelProfile.full}
            element={<ChannelProfileScreen/>}
          />
          <Route
            path={ROUTES.matchesVoted.short}
            element={<MatchesVotedScreen/>}
          />
          <Route
            path={ROUTES.competitions.create.short}
            element={<CreateTournamentForm/>}
          />
          <Route
            path={ROUTES.competitions.view.short}
            element={<Tournaments/>}
          />
          <Route
            path={ROUTES.competitions.votes.short}
            element={<CompetitionVotes/>}
          />
          <Route
            path={ROUTES.competitions.leaderboard.short}
            element={<CompetitionsLeaderboard/>}
          />
          <Route
            path={ROUTES.competitions.tipsterDetail.short}
            element={<TipsterCompetitionManagement/>}
          />
          <Route
            path={ROUTES.dashboard.short}
            element={<Dashboard/>}
          />
          <Route
            path={ROUTES.leaderboard.short}
            element={<Leaderboard/>}
          />
          <Route
            path={ROUTES.matchTracking.full}
            element={<MatchTrackingScreen/>}
          />
          <Route
            path={ROUTES.paymentResult.short}
            element={<PaymentResultScreen/>}
          />
          <Route element={<SubscriptionsRoute/>}>
            <Route
              path={ROUTES.payment.short}
              element={<PaymentScreen/>}
            />
            <Route
              path={ROUTES.userSubscriptions.short}
              element={<UserSubscriptions/>}
            />
            <Route
              path={ROUTES.tipsterSubscriptions.short}
              element={<TipsterSubscriptions/>}
            />
            <Route
              path={ROUTES.tipsterSubscriberList.short}
              element={isTipster || isAdmin ? <TipsterSubscribersList/> : <UserSubscribersList/>}
            />
          </Route>
          <Route
            path={ROUTES.tipsterList.short}
            element={
              <ProtectedRoute redirectPath={ROUTES.feed.short} isAllowed={isTipsterAdmin || isAdmin || isGuestTipster}>
                <TipsterListScreen />
              </ProtectedRoute>
            }
          />
          <Route element={<ProtectedRoute redirectPath={ROUTES.feed.short} isAllowed={isAdmin || isTipster} />}>
            <Route
              path={ROUTES.advertisements.short}
              element={<AdvertisementsScreen/>}
            />
            <Route
              path={ROUTES.advertisements.detail}
              element={<AdvertisementDetailScreen/>}
            />
          </Route>
          <Route
            path={ROUTES.bookies.short}
            element={<BookiesScreen/>}
          />
          <Route
            path={ROUTES.bookies.create}
            element={<BookieFormScreen/>}
          />
          <Route
            path={ROUTES.bookies.edit}
            element={<BookieFormScreen/>}
          />
          <Route
            path={ROUTES.bonuses.short}
            element={<BonusesScreen/>}
          />
          <Route
            path={ROUTES.bonuses.create}
            element={<BonusCreateScreen/>}
          />
          <Route
            path={ROUTES.bonuses.edit}
            element={<BonusEditScreen/>}
          />
          <Route element={<ProtectedRoute redirectPath={ROUTES.feed.short} isAllowed={isAdmin} />}>
            <Route
              path={ROUTES.rgBanners.short}
              element={<RgBannersScreen/>}
            />
          </Route>
        </Route>
        <Route path={ROUTES.login.asParent} element={<LoginRoutes/>}/>
        <Route path={ROUTES.tipsterOnboarding.asParent} element={<TipsterOnboardingRoutes/>}/>
        <Route path={ROUTES.magicLink.short} element={<MagicLinkScreen/>}/>
        <Route path={ROUTES.magicLinkSent.short} element={<MagicLinkSentScreen/>}/>
        <Route path="*" element={<Navigate to={ROUTES.feed.short} replace/>}/>
      </>
    )
  }, [ banned, isAllowed, entryScreenRedirect, hasOnboarded, initiateOnboarding, isAdmin, isTipsterAdmin ])

  const routerChildren = (
    <Suspense>
      <EmbedBridge />
      <OnboardingRedirectGuard />
      <MetaPixel />
      <TwitterPixel />
      <SignupPromptModal />
      <AppMenu
        open={appMenuOpened}
        onClose={() => AppMenuState.opened = false}
        positionX={0}
      />
      <SupportChatProvider />
      <Routes>
        <Route path={ROUTES.install.short} element={<InstallScreen/>} />
        <Route path={ROUTES.ChannelList.short} element={<ChannelListScreen/>} />
        <Route path={ROUTES.legal.short} element={<LegalScreen/>} />
        <Route path={ROUTES.invite.short} element={<VipInviteRedeemScreen/>} />
        <Route
          path={ROUTES.content.full}
          element={<CommentsScreen/>}
        />
        {SpecialRoutes}
      </Routes>
    </Suspense>
  )

  // Inside a casino iframe we use MemoryRouter so that in-app navigation
  // (e.g. /login → /feed after auth) never touches window.location.
  // Some casino hosts detect iframe URL changes and promote the new URL to
  // the top frame, breaking out of their site.
  if (window.top !== window) {
    const initialEntry = `${window.location.pathname}${window.location.search}${window.location.hash}`
    return (
      <MemoryRouter initialEntries={[ initialEntry ]}>
        {routerChildren}
      </MemoryRouter>
    )
  }

  return (
    <BrowserRouter>
      {routerChildren}
    </BrowserRouter>
  )
}

export default AppRoutes
